import Phaser from 'phaser';
import {
  BLADESMAN_ART,
  BLADESMAN_LINE,
  BLADESMAN_NAME,
  CAPY_COUNTDOWN_STEP_MS,
  CAPY_NOTICE_MS,
  CAPY_SHAKE_MS,
  CAPY_SPEED,
  CAPY_TOTAL,
  CAPY_WAVE_MS,
  CapyRun,
  rollCapys,
  type CapyKind,
} from '../../config/capybaraEvent';
import { Rng } from '../../core/rng';
import type { ProgressService } from '../../services/progressService';
import { hubProgress } from '../hubShared';
import { CAPYCOIN_SPIN_FRAMES, cycleFrames, spinCoin } from '../coin';
import { getMusicService } from '../audio/MusicService';
import { installDebugReturn } from '../debugMode';
import { CAPYSTONES_PER_RUN } from '../../config/archive';
import { CAPY_RUN_BOARD } from '../../config/leaderboard';
import { saveSyncEngine } from '../../account/saveSync';

export interface CapybaraSceneData {
  /**
   * 'event' (default) is the live Capybara Event: Capycoins and BACK TO EVENTS.
   * 'archive' is the Event Archive's replay (spec 2026-10-04): no Capycoins, a
   * Capystone per finished run, the high score on the Capy Run board, and
   * LEADERBOARD + BACK TO ARCHIVE at the end.
   */
  mode?: 'event' | 'archive';
  /** Where LEAVE / BACK go (default HubScene, EVENTS tab — the archive reopened in archive mode). */
  returnTo?: string;
  returnData?: object;
  /** Test hooks: a seeded roll and a shorter run. */
  seed?: number;
  total?: number;
}

const W = 1280;
const H = 720;
/** The four lanes capybaras run down (shuffled per wave, ±60 jitter). */
const LANES = [260, 500, 780, 1020];
const CAPY_SIZE = 84;
/** The two-frame run: the frames swap every RUN_FRAME_MS with a squash bounce. */
const RUN_FRAME_MS = 110;
const FRAMES: Record<CapyKind, readonly string[]> = {
  capy: ['capy-run-1', 'capy-run-2'],
  red: ['capy-red-1', 'capy-red-2'],
};
const D_HUD = 40;
const D_NOTICE = 45;
const D_RESULT = 50;

type Phase = 'notice' | 'countdown' | 'run' | 'done';

/**
 * The Capybara Event's minigame (spec 2026-09-15): the Bladesman's line, a
 * 3-2-1-GO, then waves of capybaras running down the field — tap the brown
 * ones (+1), avoid the red imposters (-1 and a shake). The score is paid in
 * Capycoins the instant the last capybara is resolved, before the end
 * screen; LEAVE at any point forfeits.
 */
export class CapybaraScene extends Phaser.Scene {
  /** Public so the e2e can read kinds and the score. */
  run!: CapyRun;
  private progress!: ProgressService;
  private returnTo = 'HubScene';
  private returnData: object = { tab: 'events' };
  private mode: 'event' | 'archive' = 'event';
  /** The archive run's final push to the server, awaited by LEADERBOARD. */
  private synced: Promise<void> = Promise.resolve();
  private rand: () => number = Math.random;
  private phase: Phase = 'notice';
  private sprites = new Map<number, Phaser.GameObjects.Image>();
  private scoreText!: Phaser.GameObjects.Text;
  private leaveBtn!: Phaser.GameObjects.Text;
  private waveTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super({ key: 'CapybaraScene' });
  }

  init(data: CapybaraSceneData): void {
    this.mode = data?.mode === 'archive' ? 'archive' : 'event';
    this.returnTo = data?.returnTo ?? 'HubScene';
    this.returnData =
      data?.returnData ??
      (this.mode === 'archive' ? { tab: 'events', archive: true } : { tab: 'events' });
    this.synced = Promise.resolve();
    if (data?.seed != null) {
      const rng = new Rng(data.seed);
      this.rand = () => rng.next();
    } else {
      this.rand = Math.random;
    }
    const total = data?.total != null && data.total > 0 ? Math.floor(data.total) : CAPY_TOTAL;
    this.run = new CapyRun(rollCapys(this.rand, total));
  }

  create(): void {
    this.progress = hubProgress();
    this.phase = 'notice';
    this.sprites = new Map();
    this.waveTimer = undefined;

    this.add
      .image(W / 2, H / 2, 'bg-capyrun')
      .setDisplaySize(W, H)
      .setDepth(0);

    const leave = this.add
      .text(80, 40, '← LEAVE', {
        fontSize: '20px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 14, y: 10 },
      })
      .setOrigin(0.5)
      .setDepth(D_HUD)
      .setName('capy-leave')
      .setInteractive({ useHandCursor: true });
    leave.on('pointerover', () => leave.setColor('#f5c518'));
    leave.on('pointerout', () => leave.setColor('#cfe0bd'));
    leave.on('pointerup', () => this.leave());
    this.leaveBtn = leave;

    // On its own dark pill: the farm sits at the top of the field under it.
    this.scoreText = this.add
      .text(W / 2, 40, 'Score: 0', {
        fontSize: '30px',
        color: '#ffffff',
        fontStyle: 'bold',
        backgroundColor: '#15131f',
        padding: { x: 18, y: 6 },
      })
      .setOrigin(0.5)
      .setDepth(D_HUD)
      .setName('capy-score');

    const music = getMusicService(this);
    music.playTrack('episode-selection');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      this.waveTimer?.remove();
    });

    this.showNotice();
    installDebugReturn(this);
  }

  // ── Phase 1: the Bladesman ────────────────────────────────────────────────

  /**
   * The Cheese Bladesman's line over the field: a dark panel with his
   * portrait, tap (or CAPY_NOTICE_MS) to continue into the countdown.
   */
  private showNotice(): void {
    const objs: Phaser.GameObjects.GameObject[] = [];
    // Under the HUD: LEAVE (depth D_HUD) stays tappable through the notice.
    const catcher = this.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.35)
      .setDepth(D_HUD - 1)
      .setInteractive();
    objs.push(catcher);
    const cx = W / 2;
    const cy = H / 2;
    const w = 900;
    const h = 110;
    const panel = this.add
      .rectangle(cx, cy, w, h, 0x15131f, 0.95)
      .setStrokeStyle(2, 0xffd23f)
      .setDepth(D_NOTICE)
      .setName('capy-notice')
      .setInteractive({ useHandCursor: true });
    objs.push(panel);
    if (this.textures.exists(BLADESMAN_ART)) {
      objs.push(
        this.add
          .image(cx - w / 2 + 52, cy, BLADESMAN_ART)
          .setDisplaySize(72, 72)
          .setDepth(D_NOTICE + 1),
      );
    }
    const font = 'system-ui, sans-serif';
    objs.push(
      this.add
        .text(cx - w / 2 + 100, cy - 20, `${BLADESMAN_NAME}:`, {
          fontFamily: font,
          fontSize: '20px',
          fontStyle: 'bold',
          color: '#ffd23f',
        })
        .setOrigin(0, 0.5)
        .setDepth(D_NOTICE + 1),
    );
    objs.push(
      this.add
        .text(cx - w / 2 + 100, cy + 12, BLADESMAN_LINE, {
          fontFamily: font,
          fontSize: '19px',
          fontStyle: 'bold',
          color: '#f4efe6',
          wordWrap: { width: w - 130 },
        })
        .setOrigin(0, 0.5)
        .setDepth(D_NOTICE + 1)
        .setName('capy-notice-text'),
    );
    objs.push(
      this.add
        .text(cx, cy + h / 2 + 22, 'tap to continue', {
          fontSize: '14px',
          color: '#cfd3dc',
          fontStyle: 'italic',
        })
        .setOrigin(0.5)
        .setDepth(D_NOTICE + 1),
    );
    let gone = false;
    const dismiss = () => {
      if (gone || this.phase !== 'notice') return;
      gone = true;
      this.phase = 'countdown';
      this.tweens.add({
        targets: objs,
        alpha: 0,
        duration: 300,
        onComplete: () => objs.forEach((o) => o.destroy()),
      });
      this.startCountdown();
    };
    const onTap = (_p: unknown, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
      dismiss();
    };
    catcher.on('pointerdown', onTap);
    panel.on('pointerdown', onTap);
    this.time.delayedCall(CAPY_NOTICE_MS, dismiss);
  }

  // ── Phase 2: 3, 2, 1, GO ──────────────────────────────────────────────────

  private startCountdown(): void {
    const steps = ['3', '2', '1', 'GO!'];
    const show = (i: number) => {
      if (this.phase !== 'countdown') return;
      const last = i === steps.length - 1;
      const txt = this.add
        .text(W / 2, 340, steps[i], {
          fontSize: last ? '160px' : '140px',
          color: last ? '#f5c518' : '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setStroke('#1a140a', 8)
        .setDepth(D_NOTICE)
        .setName('capy-countdown')
        .setScale(1.5);
      this.tweens.add({ targets: txt, scale: 1, duration: 250, ease: 'Back.easeOut' });
      const hold = last ? 700 : CAPY_COUNTDOWN_STEP_MS;
      this.time.delayedCall(hold, () => {
        txt.destroy();
        if (last) this.startRun();
        else show(i + 1);
      });
    };
    show(0);
  }

  // ── Phase 3: the run ──────────────────────────────────────────────────────

  private startRun(): void {
    if (this.phase !== 'countdown') return;
    this.phase = 'run';
    this.spawnWave();
    this.waveTimer = this.time.addEvent({
      delay: CAPY_WAVE_MS,
      loop: true,
      callback: () => this.spawnWave(),
    });
  }

  private spawnWave(): void {
    if (this.phase !== 'run') return;
    if (!this.run.hasNextWave()) {
      this.waveTimer?.remove();
      return;
    }
    const lanes = [...LANES];
    for (let i = lanes.length - 1; i > 0; i--) {
      const j = Math.floor(this.rand() * (i + 1));
      [lanes[i], lanes[j]] = [lanes[j], lanes[i]];
    }
    this.run.nextWave().forEach(({ index, kind }, slot) => {
      const x = lanes[slot % lanes.length] + (this.rand() * 120 - 60);
      const y = -60 - this.rand() * 40;
      const frames = FRAMES[kind];
      const img = this.add
        .image(x, y, frames[0])
        .setDisplaySize(CAPY_SIZE, CAPY_SIZE)
        .setDepth(10)
        .setName(`capy-${index}`)
        .setInteractive({ useHandCursor: true });
      img.setData('baseX', x);
      cycleFrames(this, img, frames, RUN_FRAME_MS);
      // The gallop bounce: a squash in step with the frame swap, so the run
      // reads even where the two generated frames differ only subtly.
      this.tweens.add({
        targets: img,
        scaleY: img.scaleY * 0.9,
        duration: RUN_FRAME_MS,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      img.on(
        'pointerdown',
        (_p: unknown, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
          ev.stopPropagation();
          this.onHit(index, img);
        },
      );
      this.sprites.set(index, img);
    });
    if (!this.run.hasNextWave()) this.waveTimer?.remove();
  }

  private onHit(index: number, img: Phaser.GameObjects.Image): void {
    if (this.phase !== 'run') return;
    const kind = this.run.hit(index);
    if (!kind) return;
    const { x, y } = img;
    img.destroy();
    this.sprites.delete(index);
    const red = kind === 'red';
    const pop = this.add
      .text(x, y, red ? '-1' : '+1', {
        fontSize: '36px',
        color: red ? '#ff5c5c' : '#7ed67e',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setStroke('#1a140a', 5)
      .setDepth(30);
    this.tweens.add({
      targets: pop,
      y: y - 60,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => pop.destroy(),
    });
    if (red) this.cameras.main.shake(CAPY_SHAKE_MS, 0.008);
    this.scoreText.setText(`Score: ${this.run.score}`);
    this.checkFinished();
  }

  override update(time: number, delta: number): void {
    if (this.phase !== 'run') return;
    const dy = (CAPY_SPEED * delta) / 1000;
    for (const [index, img] of [...this.sprites]) {
      img.y += dy;
      img.x = (img.getData('baseX') as number) + Math.sin(time / 90 + index) * 3;
      if (img.y > H + 60) {
        img.destroy();
        this.sprites.delete(index);
        this.run.miss(index);
      }
    }
    this.checkFinished();
  }

  private checkFinished(): void {
    if (this.phase !== 'run' || !this.run.finished()) return;
    this.phase = 'done';
    this.waveTimer?.remove();
    if (this.mode === 'archive') {
      // Banked NOW, like the event's Capycoins: the Capystone and the high
      // score, then an immediate push so the board LEADERBOARD opens onto
      // already holds this run (the sync's own debounce is three seconds).
      const stones = this.progress.awardCapystone(CAPYSTONES_PER_RUN);
      const newBest = this.progress.recordCapyScore(this.run.score);
      this.synced = (saveSyncEngine()?.flush() ?? Promise.resolve()).catch(() => undefined);
      this.time.delayedCall(400, () => this.showArchiveResult(newBest, stones));
      return;
    }
    // Banked NOW, before the ceremony: leaving mid-animation never costs it.
    const before = this.progress.capycoins();
    const after = this.progress.addCapycoins(this.run.score);
    this.time.delayedCall(400, () => this.showResult(before, after));
  }

  // ── Phase 4: Minigame Complete ────────────────────────────────────────────

  /** The Level Complete overlay's shape: title, score, the pill, the flyers. */
  private showResult(before: number, after: number): void {
    const earned = after - before;
    // The HUD gives way to the overlay: BACK TO EVENTS replaces LEAVE, and
    // the pill sits where the score counter was.
    this.leaveBtn.setVisible(false).disableInteractive();
    this.scoreText.setVisible(false);
    this.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.65)
      .setDepth(D_RESULT)
      .setInteractive();
    this.add
      .text(W / 2, H / 2 - 130, 'Minigame Complete!', {
        fontSize: '56px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 1)
      .setName('capy-result-title');
    this.add
      .text(W / 2, H / 2 - 2, `Score: ${this.run.score}`, { fontSize: '34px', color: '#ffffff' })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 1);

    const pillY = 70;
    this.add
      .rectangle(W / 2, pillY, 220, 48, 0x1a1726, 0.9)
      .setStrokeStyle(2, 0xa8703a)
      .setDepth(D_RESULT + 1);
    const pillCoin = this.add
      .image(W / 2 - 78, pillY, 'capycoin')
      .setDisplaySize(34, 34)
      .setDepth(D_RESULT + 2);
    spinCoin(this, pillCoin, CAPYCOIN_SPIN_FRAMES);
    const pillText = this.add
      .text(W / 2 - 50, pillY, `${before}`, {
        fontSize: '26px',
        color: '#e0b98a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(D_RESULT + 2)
      .setName('capy-result-balance');
    this.add
      .text(W / 2, pillY + 44, `+${earned} Capycoins`, {
        fontSize: '20px',
        color: '#e0b98a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 2);
    // Float a handful of spinning Capycoins from mid-screen into the pill,
    // bumping the total so the last one lands exactly on the new balance.
    const flyers = Math.min(earned, 8);
    let landed = 0;
    for (let i = 0; i < flyers; i++) {
      const coin = this.add
        .image(W / 2, H / 2 - 30, 'capycoin')
        .setDisplaySize(28, 28)
        .setDepth(D_RESULT + 3);
      spinCoin(this, coin, CAPYCOIN_SPIN_FRAMES);
      this.tweens.add({
        targets: coin,
        x: W / 2 - 78,
        y: pillY,
        delay: i * 90,
        duration: 600,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          coin.destroy();
          landed++;
          pillText.setText(`${before + Math.round((earned * landed) / flyers)}`);
        },
      });
    }

    const back = this.add
      .text(W / 2, H / 2 + 110, 'BACK TO EVENTS', {
        fontSize: '24px',
        color: '#f4e9c8',
        fontStyle: 'bold',
        backgroundColor: '#6b3e1e',
        padding: { x: 24, y: 12 },
      })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 2)
      .setName('capy-result-back')
      .setInteractive({ useHandCursor: true });
    back.on('pointerover', () => back.setBackgroundColor('#8a5430'));
    back.on('pointerout', () => back.setBackgroundColor('#6b3e1e'));
    back.on('pointerup', () => this.leave());
  }

  /**
   * The archive's end screen: the score, the high score, the Capystone it
   * paid, and LEADERBOARD above BACK TO ARCHIVE. No Capycoins anywhere — the
   * archive pays its own prize, never the event's currency.
   */
  private showArchiveResult(newBest: boolean, stones: number): void {
    this.leaveBtn.setVisible(false).disableInteractive();
    this.scoreText.setVisible(false);
    this.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.65)
      .setDepth(D_RESULT)
      .setInteractive();
    this.add
      .text(W / 2, H / 2 - 170, 'Minigame Complete!', {
        fontSize: '56px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 1)
      .setName('capy-result-title');
    this.add
      .text(W / 2, H / 2 - 92, `Score: ${this.run.score}`, { fontSize: '34px', color: '#ffffff' })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 1)
      .setName('capy-archive-score');
    const best = this.progress.capyBest() ?? this.run.score;
    this.add
      .text(W / 2, H / 2 - 50, newBest ? 'NEW HIGH SCORE!' : `High score: ${best}`, {
        fontSize: '22px',
        color: newBest ? '#7ed67e' : '#cfd3dc',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D_RESULT + 1)
      .setName('capy-archive-best');

    // The prize: the Capystone, popped in, with the shelf's new count.
    const stone = this.add
      .image(W / 2 - 110, H / 2 + 6, 'item-capystone')
      .setDisplaySize(52, 52)
      .setDepth(D_RESULT + 2);
    const stoneScale = stone.scale;
    stone.setScale(0);
    this.tweens.add({ targets: stone, scale: stoneScale, duration: 380, ease: 'Back.easeOut' });
    this.add
      .text(W / 2 - 74, H / 2 + 6, `+${CAPYSTONES_PER_RUN} Capystone  (${stones} in Storage)`, {
        fontSize: '20px',
        color: '#e0b98a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(D_RESULT + 2)
      .setName('capy-archive-prize');

    const button = (y: number, label: string, name: string, fill: number, hover: number) => {
      const b = this.add
        .text(W / 2, y, label, {
          fontSize: '24px',
          color: '#f4e9c8',
          fontStyle: 'bold',
          backgroundColor: `#${fill.toString(16).padStart(6, '0')}`,
          padding: { x: 24, y: 12 },
        })
        .setOrigin(0.5)
        .setDepth(D_RESULT + 2)
        .setName(name)
        .setInteractive({ useHandCursor: true });
      b.on('pointerover', () => b.setBackgroundColor(`#${hover.toString(16).padStart(6, '0')}`));
      b.on('pointerout', () => b.setBackgroundColor(`#${fill.toString(16).padStart(6, '0')}`));
      return b;
    };
    let going = false;
    const board = button(H / 2 + 92, 'LEADERBOARD', 'capy-archive-leaderboard', 0x1f5f8b, 0x2c7bb0);
    board.on('pointerup', () => {
      if (going) return;
      going = true;
      board.setText('LOADING…');
      void this.synced.then(() => {
        if (!this.scene.isActive()) return;
        this.scene.start('LeaderboardScene', {
          board: CAPY_RUN_BOARD,
          returnTo: this.returnTo,
          returnData: this.returnData,
        });
      });
    });
    const back = button(H / 2 + 162, 'BACK TO ARCHIVE', 'capy-archive-back', 0x6b3e1e, 0x8a5430);
    back.on('pointerup', () => {
      if (going) return;
      going = true;
      this.leave();
    });
  }

  private leave(): void {
    this.scene.start(this.returnTo, this.returnData);
  }
}
